/**
 * User Permissions Helpers
 *
 * Utilities for handling user permissions and role-based configurations
 * for displaying and editing user-related fields.
 */

export interface UserBasicInfo {
  uniqueId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  [key: string]: any;
}

export interface UserBasicInfoFormatted extends UserBasicInfo {
  fullName?: string;
}

export interface DriverFieldConfig {
  optionLabelKey: string;
  disabled: boolean;
  label: string;
  searchable?: boolean;
}

/**
 * Roles that can edit driver field
 */
export const DRIVER_EDIT_ROLES = ["Super Admin", "Admin"];

/**
 * Roles that have read-only access
 */
export const DRIVER_VIEW_ONLY_ROLES = ["View Only"];

/**
 * Roles that see only ID
 */
export const DRIVER_ID_ONLY_ROLES = ["Driver"];

/**
 * Formats users basic info based on the user's role
 *
 * @param users - Array of users to format
 * @param userRole - Current user's role
 * @returns Formatted users array
 *
 * @example
 * const formatted = formatUsersBasicInfoByRole(users, "View Only");
 * // Returns users with fullName field added
 */
export const formatUsersBasicInfoByRole = (
  users: UserBasicInfo[] | null | undefined,
  userRole: string
): UserBasicInfoFormatted[] => {
  if (!users) return [];

  // For View Only, add fullName field
  if (DRIVER_VIEW_ONLY_ROLES.includes(userRole)) {
    return users.map((user) => {
      const firstName = user.firstName || "";
      const lastName = user.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim();
      return {
        ...user,
        fullName: fullName || user.email
      };
    });
  }

  return users;
};

/**
 * Gets driver field configuration based on user's role
 *
 * @param userRole - Current user's role
 * @param translator - Translation function
 * @returns Field configuration object
 *
 * @example
 * const config = getDriverFieldConfigByRole("Admin", t);
 * // Returns: { optionLabelKey: "email", disabled: false, label: "Driver", searchable: true }
 */
export const getDriverFieldConfigByRole = (
  userRole: string,
  translator: any
): DriverFieldConfig => {
  // Driver role - shows uniqueId, disabled
  if (DRIVER_ID_ONLY_ROLES.includes(userRole)) {
    return {
      optionLabelKey: "uniqueId",
      disabled: true,
      searchable: false,
      label:
        translator("driver.label", { ns: "formFieldsTranslation" }) + " (ID)"
    };
  }

  // View Only - shows full name, disabled
  if (DRIVER_VIEW_ONLY_ROLES.includes(userRole)) {
    return {
      optionLabelKey: "fullName",
      disabled: true,
      searchable: false,
      label: translator("driver.label", { ns: "formFieldsTranslation" })
    };
  }

  // Admin/Super Admin - shows email, enabled
  if (DRIVER_EDIT_ROLES.includes(userRole)) {
    return {
      optionLabelKey: "email",
      disabled: false,
      searchable: true,
      label: translator("driver.label", { ns: "formFieldsTranslation" })
    };
  }

  // Default (Manager, etc) - shows email, disabled
  return {
    optionLabelKey: "email",
    disabled: true,
    searchable: false,
    label: translator("driver.label", { ns: "formFieldsTranslation" })
  };
};

/**
 * Checks if user can edit driver field
 *
 * @param userRole - Current user's role
 * @returns true if user can edit
 */
export const canEditDriverField = (userRole: string): boolean => {
  return DRIVER_EDIT_ROLES.includes(userRole);
};

/**
 * Gets the display value for a driver based on role
 *
 * @param driver - Driver object
 * @param userRole - Current user's role
 * @returns Display string
 */
export const getDriverDisplayValue = (
  driver: UserBasicInfo | null | undefined,
  userRole: string
): string => {
  if (!driver) return "-";

  // Driver role - show uniqueId
  if (DRIVER_ID_ONLY_ROLES.includes(userRole)) {
    return driver.uniqueId;
  }

  // View Only - show full name
  if (DRIVER_VIEW_ONLY_ROLES.includes(userRole)) {
    const firstName = driver.firstName || "";
    const lastName = driver.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || driver.email;
  }

  // Everyone else - show email
  return driver.email;
};
